import clsx from "clsx";
import { useState } from "react";
import { Form } from "react-final-form";
import { useTranslation } from "react-i18next";
import AuthService from "src/services/authService";
import { getSettings } from "src/services/configService";
import { useDispatch } from "src/store";
import { setCurrentWorkspaceId } from "src/store/app";
import { TextField } from "src/ui/shared/form/TextField";
import LanguageSwitcher from "src/ui/shared/LanguageSwitcher/LanguageSwitcher";
import { copyToClipboard } from "src/ui/utils/clipboard";

import { useClaimStarterSubscriptionMutation, useLazyGetWorkspacesQuery } from "@dashboard/workspaces/api";
import { Box, Paper, Typography } from "@mui/material";
import { CustomAlert } from "@shared/CustomAlert/CustomAlert";
import { PrimaryButton } from "@shared/CustomButton";
import { SectionPanel } from "@shared/SectionPanel/SectionPanel";
import { showErrorToast, showSuccessToast } from "@shared/toasts/Toasts";
import { getErrorMessage } from "@shared/toasts/useQueryErrorToast";

import createWorkspaceImage from "../../assets/CreateWorkspace_400h.webp";
import inviteToWorkspaceImage from "../../assets/InviteToWorkspace_400h.webp";

import { useWorkspaceClaimStyles } from "./WorkspaceClaimSection.styles";

type WorkspaceClaimOptionCardProps = {
    isSelected: boolean;
    onSelect: () => void;
    classes: ReturnType<typeof useWorkspaceClaimStyles>["classes"];
};

const JoinWorkspaceCard = ({ isSelected, onSelect, classes }: WorkspaceClaimOptionCardProps) => {
    const { t } = useTranslation(["common"]);
    return (
        <SectionPanel
            disablePadding
            selected={isSelected}
            role="button"
            tabIndex={0}
            className={classes.optionCard}
            onClick={onSelect}
        >
            <Box className={classes.optionImageWrapper}>
                <Box className={clsx(classes.indicator, isSelected && classes.indicatorSelected)} aria-hidden>
                    {isSelected ? <i className="fa-solid fa-check" /> : null}
                </Box>
                <img src={inviteToWorkspaceImage} alt="" className={classes.optionImage} />
            </Box>
            <Box className={classes.optionBody}>
                <Typography variant="overline" className={classes.optionPlan}>
                    {t("common:section.tutorial.badge.enterprise")}
                </Typography>
                <Typography variant="subtitle1" className={classes.optionTitle}>
                    {t("common:section.tutorial.button.joinExistingWorkspace")}
                </Typography>
                <Typography variant="body2" className={classes.optionDescription}>
                    {t("common:section.tutorial.description.joinWorkspace")}
                </Typography>
            </Box>
        </SectionPanel>
    );
};

const CreateWorkspaceCard = ({ isSelected, onSelect, classes }: WorkspaceClaimOptionCardProps) => {
    const { t } = useTranslation(["common"]);
    return (
        <SectionPanel
            disablePadding
            selected={isSelected}
            role="button"
            tabIndex={0}
            className={classes.optionCard}
            onClick={onSelect}
            onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    onSelect();
                }
            }}
        >
            <Box className={classes.optionImageWrapper}>
                <Box className={clsx(classes.indicator, isSelected && classes.indicatorSelected)} aria-hidden>
                    {isSelected ? <i className="fa-solid fa-check" /> : null}
                </Box>
                <img src={createWorkspaceImage} alt="" className={classes.optionImage} />
            </Box>
            <Box className={classes.optionBody}>
                <Typography variant="overline" className={classes.optionPlan}>
                    {t("common:section.tutorial.badge.free")}
                </Typography>
                <Typography variant="subtitle1" className={classes.optionTitle}>
                    {t("common:section.tutorial.button.createFreeWorkspace")}
                </Typography>
                <Typography variant="body2" className={classes.optionDescription}>
                    {t("common:section.tutorial.description.createWorkspace")}
                </Typography>
            </Box>
        </SectionPanel>
    );
};

type FormValues = {
    action: "join" | "create";
    email: string;
};

export const WorkspaceClaimSection = () => {
    const { t } = useTranslation(["common"]);
    const { classes } = useWorkspaceClaimStyles();
    const user = AuthService.getCurrentUser();
    const { freeWorkspaceEnabled } = getSettings().features;
    const dispatch = useDispatch();
    const [claimStarterSubscription] = useClaimStarterSubscriptionMutation();
    const [getWorkspaces] = useLazyGetWorkspacesQuery({});
    const [isClaiming, setIsClaiming] = useState(false);
    const [hasClaimed, setHasClaimed] = useState(false);

    const claimPlan = async () => {
        try {
            await claimStarterSubscription().unwrap();
            setHasClaimed(true);
            return true;
        } catch (error) {
            showErrorToast(getErrorMessage(error));
            return false;
        }
    };

    const openClaimedWorkspace = async () => {
        const workspaces = await getWorkspaces({})
            .unwrap()
            .catch(() => []);
        const workspace = workspaces[0];
        if (!workspace) {
            showErrorToast(t("common:section.tutorial.toast.claimedWorkspaceNotFound"));
            return;
        }
        dispatch(setCurrentWorkspaceId(workspace.id));
        showSuccessToast(t("common:section.tutorial.toast.starterSubscriptionClaimSuccess"));
    };

    const onFreePlanConfirm = async () => {
        setIsClaiming(true);
        try {
            if (hasClaimed || (await claimPlan())) {
                await openClaimedWorkspace();
            }
        } finally {
            setIsClaiming(false);
        }
    };

    return (
        <Box className={classes.container}>
            <Form<FormValues>
                onSubmit={() => undefined}
                initialValues={{ action: freeWorkspaceEnabled ? "create" : "join", email: user?.email || "" }}
                render={({ form, handleSubmit, values }) => (
                    <Box component="form" onSubmit={handleSubmit} className={classes.content}>
                        <Box className={classes.titleContainer}>
                            <Box className={classes.languageSwitcher}>
                                <LanguageSwitcher fullWidth />
                            </Box>
                        </Box>
                        {freeWorkspaceEnabled ? (
                            <>
                                <Typography variant="body1" className={classes.description}>
                                    {t("common:section.tutorial.description.yourWorkspaceCta")}
                                </Typography>

                                <Box className={classes.cardList}>
                                    <CreateWorkspaceCard
                                        isSelected={values?.action === "create"}
                                        onSelect={() => form.change("action", "create")}
                                        classes={classes}
                                    />
                                    <JoinWorkspaceCard
                                        isSelected={values?.action === "join"}
                                        onSelect={() => form.change("action", "join")}
                                        classes={classes}
                                    />
                                </Box>
                            </>
                        ) : null}

                        <Box className={classes.dynamicSection}>
                            {values?.action === "join" ? (
                                <Paper className={classes.detailPanel} elevation={0}>
                                    <CustomAlert severity="info">{t("common:section.tutorial.alert.getStarted")}</CustomAlert>
                                    <Box className={classes.inputBox}>
                                        <Box className={classes.emailField}>
                                            <TextField
                                                name="email"
                                                label={t("common:section.tutorial.input.email.caption")}
                                                fullWidth
                                                disabled
                                            />
                                        </Box>
                                        <PrimaryButton
                                            size="medium"
                                            onClick={() => void copyToClipboard(values?.email)}
                                            startIcon={
                                                <Box component="span">
                                                    <i className="fa-regular fa-copy" />
                                                </Box>
                                            }
                                        >
                                            {t("common:button.copy")}
                                        </PrimaryButton>
                                    </Box>
                                </Paper>
                            ) : (
                                <Box className={classes.actionCenter}>
                                    <PrimaryButton onClick={onFreePlanConfirm} disabled={isClaiming} loading={isClaiming}>
                                        {t("common:section.tutorial.button.startFreePlanNow")}
                                    </PrimaryButton>
                                </Box>
                            )}
                        </Box>
                    </Box>
                )}
            />
        </Box>
    );
};
